import React, { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Check } from 'lucide-react';
interface Order {
  order_id: string;
  closed: string;
  side: 'Buy' | 'Sell';
  type: 'Market' | 'Limit' | 'Stop loss';
  pair: string;
  status: 'Filled' | 'Partially filled' | 'Canceled';
  avg_price: number | null;
  executed_qty: number;
  cost: number | null;
}
const mockOrdersData: Order[] = [{
  order_id: 'OP6J2U',
  closed: '1/18/26 4:43 PM',
  side: 'Sell',
  type: 'Market',
  pair: 'BTC/USD',
  status: 'Canceled',
  avg_price: null,
  executed_qty: 0,
  cost: null
}, {
  order_id: 'OA4PAN',
  closed: '1/18/26 4:43 PM',
  side: 'Sell',
  type: 'Market',
  pair: 'BTC/USD',
  status: 'Filled',
  avg_price: 95078.1,
  executed_qty: 0.0008267,
  cost: 78.6
}, {
  order_id: 'OWY5CU',
  closed: '1/15/26 11:35 PM',
  side: 'Buy',
  type: 'Limit',
  pair: 'BTC/USD',
  status: 'Filled',
  avg_price: 95790.6,
  executed_qty: 0.0008267,
  cost: 79.19
}, {
  order_id: 'OTXWZX',
  closed: '1/15/26 5:59 PM',
  side: 'Buy',
  type: 'Market',
  pair: 'BTC/USD',
  status: 'Canceled',
  avg_price: null,
  executed_qty: 0,
  cost: null
}, {
  order_id: 'OHXQWC',
  closed: '12/12/25 4:18 PM',
  side: 'Sell',
  type: 'Market',
  pair: 'BTC/USD',
  status: 'Filled',
  avg_price: 91772.8,
  executed_qty: 0.00115416,
  cost: 105.92
}];
// Helper to get icon URL
const getIconUrl = (pair: string) => {
  const symbol = pair.split('/')[0].toLowerCase();
  const map: Record<string, string> = {
    btc: 'bitcoin-btc',
    eth: 'ethereum-eth',
    sol: 'solana-sol',
    usdt: 'tether-usdt',
    bnb: 'bnb-bnb',
    xrp: 'xrp-xrp',
    ada: 'cardano-ada',
    doge: 'dogecoin-doge'
  };
  const slug = map[symbol] || `${symbol}-${symbol}`;
  return `https://cryptologos.cc/logos/${slug}-logo.svg`;
};
export function OrdersTab() {
  const [selectedPairs, setSelectedPairs] = useState<string[]>([]);
  const [orderEvent, setOrderEvent] = useState('opened_or_closed');
  const [showPairFilter, setShowPairFilter] = useState(false);
  const [showEventFilter, setShowEventFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const clearFilters = () => {
    setSelectedPairs([]);
    setOrderEvent('opened_or_closed');
    setDateFrom('');
    setDateTo('');
  };
  const togglePair = (pair: string) => {
    if (selectedPairs.includes(pair)) {
      setSelectedPairs(selectedPairs.filter(p => p !== pair));
    } else {
      setSelectedPairs([...selectedPairs, pair]);
    }
  };
  const hasActiveFilters = selectedPairs.length > 0 || orderEvent !== 'opened_or_closed' || dateFrom || dateTo;
  // Filter logic (simplified for mock data)
  const filteredData = mockOrdersData.filter(order => {
    if (selectedPairs.length > 0 && !selectedPairs.includes(order.pair)) return false;
    // Event filter logic would go here
    return true;
  });
  const totalItems = filteredData.length;
  const itemsPerPage = 25;
  const currentPage = 1;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  return <div className="space-y-4" data-id="element-371">
      {/* Filters */}
      <div className="glass-border rounded-[20px] p-4" data-id="element-372">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-373">
          {/* Pairs Filter */}
          <div className="relative" data-id="element-374">
            <button onClick={() => setShowPairFilter(!showPairFilter)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${showPairFilter ? 'bg-background-elevated text-text-primary ring-1 ring-brand-purple' : 'bg-background-elevated hover:bg-background-elevated/80 text-text-primary'}`} data-id="element-375">
              Select pairs
              {selectedPairs.length > 0 && <span className="bg-brand-purple text-white text-[10px] px-1.5 py-0.5 rounded-full" data-id="element-376">
                  {selectedPairs.length}
                </span>}
              <ChevronDown size={16} data-id="element-377" />
            </button>
            {showPairFilter && <>
                <div className="fixed inset-0 z-20" onClick={() => setShowPairFilter(false)} data-id="element-378" />
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 p-3 max-h-80 overflow-y-auto" data-id="element-379">
                  <div className="space-y-1" data-id="element-380">
                    {['BTC/USD', 'ETH/USD', 'SOL/USD', 'BTC/EUR'].map(pair => <button key={pair} onClick={() => togglePair(pair)} className="w-full flex items-center gap-3 p-2 hover:bg-background-secondary rounded-lg transition-colors text-left group" data-id="element-381">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedPairs.includes(pair) ? 'bg-brand-purple border-brand-purple' : 'border-text-secondary group-hover:border-text-primary'}`} data-id="element-382">
                            {selectedPairs.includes(pair) && <Check size={10} className="text-white" data-id="element-383" />}
                          </div>
                          <span className="text-sm text-text-primary" data-id="element-384">
                            {pair}
                          </span>
                        </button>)}
                  </div>
                </div>
              </>}
          </div>

          {/* Order Event Filter */}
          <div className="relative" data-id="element-385">
            <button onClick={() => setShowEventFilter(!showEventFilter)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${showEventFilter ? 'bg-background-elevated text-text-primary ring-1 ring-brand-purple' : 'bg-background-elevated hover:bg-background-elevated/80 text-text-primary'}`} data-id="element-386">
              Order event
              <ChevronDown size={16} data-id="element-387" />
            </button>
            {showEventFilter && <>
                <div className="fixed inset-0 z-20" onClick={() => setShowEventFilter(false)} data-id="element-388" />
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 p-3" data-id="element-389">
                  <div className="space-y-1" data-id="element-390">
                    {[{
                  value: 'opened_or_closed',
                  label: 'When order was opened or closed'
                }, {
                  value: 'opened_only',
                  label: 'When order was opened only'
                }, {
                  value: 'closed_only',
                  label: 'When order was closed only'
                }].map(option => <button key={option.value} onClick={() => setOrderEvent(option.value)} className="w-full flex items-center justify-between p-2 hover:bg-background-secondary rounded-lg transition-colors text-left group" data-id="element-391">
                        <span className={`text-sm ${orderEvent === option.value ? 'text-text-primary font-medium' : 'text-text-secondary group-hover:text-text-primary'}`} data-id="element-392">
                          {option.label}
                        </span>
                        {orderEvent === option.value && <Check size={16} className="text-brand-purple" data-id="element-393" />}
                      </button>)}
                  </div>
                </div>
              </>}
          </div>

          {/* Date Range */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto" data-id="element-394">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" className="px-4 py-2 min-h-[44px] w-full sm:w-auto bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-395" />
            <span className="text-text-secondary hidden sm:inline" data-id="element-396">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" className="px-4 py-2 min-h-[44px] w-full sm:w-auto bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-397" />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-text-secondary hover:text-text-primary transition-colors text-sm" data-id="element-398">
              Clear filters
            </button>}
        </div>
      </div>

      {/* Table */}
      <div className="glass-border rounded-[20px] overflow-hidden" data-id="element-399">
        <div className="overflow-x-auto" data-id="element-400">
          <table className="w-full min-w-[800px]" data-id="element-401">
            <thead className="bg-background-elevated/50" data-id="element-402">
              <tr className="border-b border-border-subtle" data-id="element-403">
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-404">
                  Closed
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-405">
                  Side
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-406">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-407">
                  Pair
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-408">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-409">
                  Average Price
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-410">
                  Quantity Executed
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-411">
                  Cost
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-412">
                  ID
                </th>
              </tr>
            </thead>
            <tbody data-id="element-413">
              {filteredData.map(order => <tr key={order.order_id} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-414">
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-415">
                    {order.closed}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-416">
                    {order.side}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-417">
                    {order.type}
                  </td>
                  <td className="px-6 py-4" data-id="element-418">
                    <div className="flex items-center gap-2" data-id="element-419">
                      <img src={getIconUrl(order.pair)} alt={order.pair} className="w-5 h-5 object-contain" data-id="element-420" />
                      <span className="text-sm font-medium text-text-primary" data-id="element-421">
                        {order.pair}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-422">
                    <span className={`text-xs px-2 py-1 rounded ${order.status === 'Filled' ? 'bg-success/10 text-success' : order.status === 'Partially filled' ? 'bg-warning/10 text-warning' : 'bg-danger/10 text-danger'}`} data-id="element-423">
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-424">
                    {order.avg_price ? <div className="font-numbers text-sm text-text-primary" data-id="element-425">
                        {order.avg_price.toLocaleString('en-US', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1
                  })}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-426">
                          USD
                        </span>
                      </div> : <span className="text-text-secondary text-sm" data-id="element-427">-</span>}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-428">
                    {order.executed_qty > 0 ? <div className="font-numbers text-sm text-text-primary" data-id="element-429">
                        {order.executed_qty.toFixed(8)}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-430">
                          BTC
                        </span>
                      </div> : <span className="text-text-secondary text-sm" data-id="element-431">-</span>}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-432">
                    {order.cost ? <div className="font-numbers text-sm text-text-primary" data-id="element-433">
                        {order.cost.toFixed(2)}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-434">
                          USD
                        </span>
                      </div> : <span className="text-text-secondary text-sm" data-id="element-435">-</span>}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-436">
                    <div className="font-numbers text-xs text-text-secondary" data-id="element-437">
                      {order.order_id}
                    </div>
                  </td>
                </tr>)}
              {filteredData.length === 0 && <tr data-id="element-438">
                  <td colSpan={9} className="px-6 py-8 text-center text-text-secondary" data-id="element-439">
                    No orders found matching your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-border-subtle flex items-center justify-between" data-id="element-440">
          <div className="text-sm text-text-secondary" data-id="element-441">
            {totalItems > 0 ? `1 - ${Math.min(itemsPerPage, totalItems)} of ${totalItems}` : '0 of 0'}
          </div>
          <div className="flex items-center gap-2" data-id="element-442">
            <button disabled={currentPage === 1} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-443">
              <ChevronLeft size={16} data-id="element-444" />
            </button>
            <div className="text-sm font-medium text-text-primary px-2" data-id="element-445">
              Page {currentPage} of {totalPages}
            </div>
            <button disabled={currentPage === totalPages} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-446">
              <ChevronRight size={16} data-id="element-447" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}